import { useState } from 'react';
import {
    BodyMediumRegular,
    Button,
    ButtonSize,
    ButtonVariant,
    H6Emphasized,
    LabelRegular,
    MultilineInput,
    Picker,
    Spinner,
    useToastController,
    XStack,
    YStack,
} from 'smoothie';

import {
    MutationErrorToastOptions,
    MutationSuccessToastOptions,
} from '../../common/components/Toasts';
import {
    useGetFruitSliceListener,
    useSimulateFruitSliceListener,
} from '../hooks/useFruitSliceListeners';
import { useGetFruitSlice } from '../hooks/useFruitSlices';

const isRecord = (value: unknown): value is Record<string, unknown> =>
    value != null && typeof value === 'object' && !Array.isArray(value);

const parsePayload = (payload: string): Record<string, unknown> | undefined => {
    if (payload.trim().length === 0) {
        return undefined;
    }

    const parsed: unknown = JSON.parse(payload);
    if (!isRecord(parsed)) {
        throw new Error('Event data must be a JSON object.');
    }

    return parsed;
};

export const FruitSliceListenerPage = ({
    sliceId,
    listenerId,
}: {
    sliceId: string;
    listenerId: string;
}) => {
    const toast = useToastController();
    const [selectedEvent, setSelectedEvent] = useState('');
    const [payload, setPayload] = useState('{}');
    const { data: slice, isPending: isLoadingSlice } =
        useGetFruitSlice(sliceId);
    const { data: listener, isPending: isLoadingListener } =
        useGetFruitSliceListener(sliceId, listenerId);
    const simulate = useSimulateFruitSliceListener(sliceId, listenerId);

    if (isLoadingSlice || isLoadingListener) {
        return (
            <YStack flex={1} alignItems="center" justifyContent="center">
                <Spinner />
            </YStack>
        );
    }

    if (slice == null || listener == null) {
        return (
            <YStack padding="$6">
                <BodyMediumRegular>Listener not found.</BodyMediumRegular>
            </YStack>
        );
    }

    const event =
        selectedEvent.length > 0
            ? selectedEvent
            : (listener.eventTypes[0] ?? '');

    const handleSimulate = async (): Promise<void> => {
        try {
            await simulate.mutateAsync({
                event,
                data: parsePayload(payload),
            });
            toast.show('Event queued', MutationSuccessToastOptions);
        } catch (error) {
            toast.show(
                'Unable to simulate event',
                MutationErrorToastOptions(error),
            );
        }
    };

    return (
        <XStack
            width="100%"
            height="calc(100vh - 120px)"
            minHeight={600}
            padding="$4"
            gap="$4"
            alignItems="stretch"
        >
            <YStack flex={1} minWidth={0} gap="$2">
                <H6Emphasized>Source</H6Emphasized>
                <MultilineInput
                    value={listener.source}
                    editable={false}
                    numberOfLines={30}
                    fontFamily="$monoBody"
                    flex={1}
                />
            </YStack>

            <YStack
                width="40%"
                minWidth={320}
                gap="$4"
                padding="$4"
                backgroundColor="$gray100"
                borderRadius="$4"
            >
                <YStack gap="$1">
                    <H6Emphasized>Simulate event</H6Emphasized>
                    <BodyMediumRegular color="$gray1000">
                        Queue a test invocation of {listener.name}.
                    </BodyMediumRegular>
                </YStack>

                <YStack gap="$2">
                    <LabelRegular>Event</LabelRegular>
                    <Picker<string>
                        value={event}
                        options={listener.eventTypes.map((eventType) => ({
                            label: eventType,
                            value: eventType,
                        }))}
                        onChange={setSelectedEvent}
                    />
                </YStack>

                <YStack flex={1} gap="$2">
                    <LabelRegular>Event data (JSON)</LabelRegular>
                    <MultilineInput
                        value={payload}
                        onChangeText={setPayload}
                        numberOfLines={18}
                        fontFamily="$monoBody"
                        flex={1}
                    />
                </YStack>

                <Button
                    text="Simulate"
                    size={ButtonSize.SMALL}
                    variant={ButtonVariant.PRIMARY}
                    disabled={
                        !slice.canWrite ||
                        event.length === 0 ||
                        simulate.isPending
                    }
                    onPress={() => {
                        void handleSimulate();
                    }}
                />
                {!slice.canWrite && (
                    <BodyMediumRegular color="$gray1000">
                        Write access is required to simulate events.
                    </BodyMediumRegular>
                )}
            </YStack>
        </XStack>
    );
};
